/**
 * The signed-in person's menu in the top bar, shared by webmail and the
 * platform console: who they are, style, light or dark, and Sign out.
 */

import { useEffect, useRef, useState } from 'react';

import { LOOKS, type Look, type Mode, type Theme } from '../theme';

/** What a look changes, in the user's words. */
export const LOOK_LABEL: Record<Look, string> = {
  signal: 'Standard',
  quiet: 'Comfortable',
  console: 'Compact',
  outlook: 'Outlook',
};

/**
 * Who is signed in, and their own preferences: style, light or dark, and
 * signing out. One menu instead of controls spread across the header.
 */
export function AccountMenu({
  name,
  email,
  initials,
  theme,
  setLook,
  setMode,
  onSignOut,
  children,
}: {
  name: string;
  email: string;
  initials: string;
  theme: Theme;
  setLook: (look: Look) => void;
  setMode: (mode: Mode) => void;
  onSignOut: () => void;
  /** Rows of the app's own preferences, above Sign out. */
  children?: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) {
      return;
    }
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (
        e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  return (
    <div className="account" ref={box}>
      <button
        type="button"
        className="account-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="account-name">{name}</span>
      </button>
      {open && (
        <div className="account-menu" role="menu">
          <div className="account-who">
            <strong>{name}</strong>
            <span className="addr">{email}</span>
            <span className="sso">Signed in with single sign-on</span>
          </div>
          <div className="account-row">
            <span>Style</span>
            <div className="seg" role="group" aria-label="Style">
              {LOOKS.map((look) => (
                <button
                  type="button"
                  key={look}
                  aria-pressed={theme.look === look}
                  onClick={() => setLook(look)}
                >
                  {LOOK_LABEL[look]}
                </button>
              ))}
            </div>
          </div>
          <div className="account-row">
            <span>Appearance</span>
            <div className="seg" role="group" aria-label="Appearance">
              {(['light', 'dark'] as const).map((mode) => (
                <button
                  type="button"
                  key={mode}
                  aria-pressed={theme.mode === mode}
                  onClick={() => setMode(mode)}
                >
                  {mode === 'light' ? 'Light' : 'Dark'}
                </button>
              ))}
            </div>
          </div>
          {children}
          <button type="button" role="menuitem" className="account-signout" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** Two letters for the avatar, from a name or an address. */
export function initialsOf(name: string): string {
  const parts = name.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]![0] ?? '';
  const second = parts.length > 1 ? (parts[1]![0] ?? '') : '';
  return (first + second).toUpperCase();
}
